<script setup lang="ts">
import { bitable, ViewType } from '@lark-base-open/js-sdk'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { FileText, Settings, Loader2, CheckCircle, XCircle, Download, AlertCircle } from 'lucide-vue-next'
import { toast } from '@/utils/toast'
import { formatFieldValue } from '@/utils/fieldFormatter'
import { useTenantStore } from '@/stores/tenant'
import { useGenerationStore } from '@/stores/generation'
import { storeToRefs } from 'pinia'
import EmptyTemplateState from '@/components/EmptyTemplateState.vue'
import type { Template } from '@/types'

interface Props {
  activeTemplate: Template | null
  allTemplates: Template[]
  baseToken: string
  tableId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'template-updated': []
  'template-deleted': []
  'template-selected': [templateId: string]
  'create-template': []
}>()

const { t } = useI18n()
const router = useRouter()
const tenantStore = useTenantStore()
const generationStore = useGenerationStore()
const { isPro } = storeToRefs(tenantStore)
const { jobs: storeJobs } = storeToRefs(generationStore)

// Local state for record selection display
const currentRecord = ref<string | null>(null)
const selectedRecordCount = ref(0)

// Computed
const hasTemplateFile = computed(() => !!props.activeTemplate?.r2_key)

// Handle download click - triggers direct download
const handleDownload = (url: string, filename: string) => {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

// Handle generation (single or bulk - store handles the logic)
const handleGenerate = async () => {
  if (!props.activeTemplate) return

  if (!hasTemplateFile.value) {
    toast.warning(t('noTemplateUploaded'))
    router.push('/template-setup')
    return
  }

  const result = await generationStore.startGeneration(
    props.baseToken,
    props.tableId,
    props.activeTemplate
  )

  // Show appropriate toast based on result
  if (result.error === 'NO_RECORDS_SELECTED') {
    toast.warning(t('pleaseSelectRecord'))
    return
  }

  if (result.error === 'GENERATION_IN_PROGRESS') {
    toast.warning(t('generationAlreadyInProgress'))
    return
  }

  if (result.error === 'TOO_MANY_RECORDS') {
    toast.warning(t('tooManyRecordsSelected', { max: result.maxRecords || 15 }))
    return
  }

  if (result.error) {
    console.error('Generation failed:', result.error)
    toast.error(result.error)
    return
  }

  // Generation started successfully - panel will show progress
  // Don't show toast here since the user can see the progress panel
}

// Load current record info for display
const loadCurrentRecord = async () => {
  try {
    const selection = await bitable.base.getSelection()

    if (!selection.tableId) {
      currentRecord.value = null
      selectedRecordCount.value = 0
      return
    }

    // Get the current table and active view
    const table = await bitable.base.getTableById(selection.tableId)
    const view = await table.getActiveView()
    const viewType = await view.getType()

    let selectedRecordIds: string[] = []

    // Only Grid view supports getSelectedRecordIdList
    if (viewType === ViewType.Grid) {
      // @ts-ignore - method exists at runtime but not in types
      selectedRecordIds = await view.getSelectedRecordIdList()

      // If no checkboxes are checked but a row is clicked/active, treat it as selected
      if (selectedRecordIds.length === 0 && selection.recordId) {
        selectedRecordIds = [selection.recordId]
      }
    } else {
      // For non-grid views, fall back to single selection from getSelection
      if (selection.recordId) {
        selectedRecordIds = [selection.recordId]
      }
    }

    selectedRecordCount.value = selectedRecordIds.length

    // If exactly one record is selected, load its display value
    if (selectedRecordIds.length === 1 && selectedRecordIds[0]) {
      const recordId = selectedRecordIds[0]
      const record = await table.getRecordById(recordId)
      const fieldMeta = await table.getFieldMetaList()

      // Find primary field
      const primaryField = fieldMeta.find(f => f.isPrimary)

      if (primaryField) {
        const value = record.fields[primaryField.id]
        const formatted = formatFieldValue(value, primaryField.type, primaryField.property)
        currentRecord.value = typeof formatted === 'string' ? formatted : (formatted ? String(formatted) : null)
      } else {
        currentRecord.value = null
      }
    } else {
      currentRecord.value = null
    }
  } catch (error) {
    console.error('Failed to load current record:', error)
    currentRecord.value = null
    selectedRecordCount.value = 0
  }
}

// Polling interval for checkbox selection changes
let selectionPollInterval: ReturnType<typeof setInterval> | null = null
let lastSelectedCount = 0

onMounted(async () => {
  await loadCurrentRecord()

  // Listen for selection change events
  bitable.base.onSelectionChange(async (e) => {
    lastSelectedCount = e.data.recordId ? 1 : 0
    await loadCurrentRecord()
  })

  // Poll for checkbox selection changes (SDK has no event for this)
  selectionPollInterval = setInterval(async () => {
    try {
      const selection = await bitable.base.getSelection()
      if (!selection.tableId) return

      const table = await bitable.base.getTableById(selection.tableId)
      const view = await table.getActiveView()
      const viewType = await view.getType()

      if (viewType === ViewType.Grid) {
        // @ts-ignore
        const selectedRecordIds = await view.getSelectedRecordIdList()
        const currentCount = selectedRecordIds.length

        if (currentCount !== lastSelectedCount) {
          lastSelectedCount = currentCount
          await loadCurrentRecord()
        }
      }
    } catch (error) {
      console.error('Error polling selection:', error)
    }
  }, 500)
})

onUnmounted(() => {
  if (selectionPollInterval) {
    clearInterval(selectionPollInterval)
  }
})
</script>

<template>
  <!-- Empty State - No Templates -->
  <EmptyTemplateState
    v-if="!allTemplates || allTemplates.length === 0"
    @create-template="emit('create-template')"
  />

  <!-- Main View -->
  <div v-else class="h-full bg-white flex flex-col">
    <!-- Main Content Container -->
    <div class="flex-1 overflow-y-auto">
      <div class="flex flex-col items-center p-8 min-h-full">

      <!-- Setup Required State -->
      <div v-if="!hasTemplateFile" class="max-w-md text-center">
        <div class="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span class="text-4xl">⚙️</span>
        </div>
        <h2 class="text-2xl font-semibold text-gray-800 mb-3">{{ t('setupRequired') }}</h2>
        <p class="text-gray-600 mb-6">
          {{ t('uploadTemplateFirst') }}
        </p>
        <button
          @click="router.push('/template-setup')"
          class="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
        >
          <Settings :size="20" />
          {{ t('goToSetup') }}
        </button>
      </div>

      <!-- Ready State - Generation Focus -->
      <div v-else class="max-w-2xl w-full">
        <!-- No Record Selected Message -->
        <div v-if="selectedRecordCount === 0 && !generationStore.isTemplateGenerating(props.activeTemplate?.id ?? '')" class="text-center mb-8">
          <p class="text-lg text-gray-500">{{ t('pleaseSelectRecord') }}</p>
        </div>

        <!-- Multiple Records Selected Message -->
        <div v-else-if="selectedRecordCount > 1 && !generationStore.isTemplateGenerating(props.activeTemplate?.id ?? '')" class="text-center mb-8">
          <p class="text-lg text-gray-500">{{ selectedRecordCount }} {{ t('recordsSelected') }}</p>
          <!-- Warning when too many records selected -->
          <p v-if="selectedRecordCount > 15" class="text-sm text-orange-600 mt-2 flex items-center justify-center gap-1">
            <AlertCircle :size="16" />
            {{ t('maxRecordsWarning', { max: 15 }) }}
          </p>
        </div>

        <!-- Generate Buttons (always show unless this template is generating) -->
        <div v-if="!generationStore.isTemplateGenerating(props.activeTemplate?.id ?? '')" class="flex justify-center gap-4 mb-12">
          <!-- Main Generate Button (Only show for 0 or 1 record selected) -->
          <button
            v-if="selectedRecordCount <= 1"
            @click="handleGenerate"
            :disabled="!currentRecord"
            class="px-12 py-5 bg-blue-600 text-white rounded-xl font-bold text-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <FileText :size="24" />
            <span v-if="currentRecord">{{ t('generateFor', { record: currentRecord }) }}</span>
            <span v-else>{{ t('generateDoc') }}</span>
          </button>

          <!-- Bulk Generation - Only show when more than 1 record selected -->
          <div v-if="selectedRecordCount > 1" class="relative">
            <div v-if="!isPro" class="absolute -top-2 -right-2 z-10">
              <span class="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">PRO</span>
            </div>
            <button
              @click="handleGenerate"
              :disabled="!isPro || selectedRecordCount > 15"
              :class="[
                'px-12 py-5 rounded-xl font-bold text-xl transition-all flex items-center gap-3 shadow-lg',
                isPro && selectedRecordCount <= 15
                  ? 'bg-green-600 text-white hover:bg-green-700 hover:shadow-xl transform hover:scale-105'
                  : 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 text-gray-500 opacity-60 cursor-not-allowed'
              ]"
            >
              <span class="text-2xl">⚡</span>
              <span>{{ t('bulkGeneration') }}</span>
            </button>
          </div>
        </div>

        <!-- Progress Panels -->
        <div v-if="storeJobs.length > 0" class="space-y-4 w-full max-w-xl mx-auto">
          <div
            v-for="job in storeJobs"
            :key="job.templateId"
            class="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
          >
            <!-- Header -->
            <div class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <Loader2 v-if="job.active" :size="20" class="animate-spin text-blue-600" />
                <CheckCircle v-else-if="job.status === 'completed'" :size="20" class="text-green-600" />
                <XCircle v-else :size="20" class="text-orange-600" />
                <div class="flex flex-col">
                  <span class="font-semibold text-gray-700">
                    {{ job.active ? t('generatingDocuments') : t('generationComplete') }}
                  </span>
                  <span class="text-xs text-gray-500">{{ job.templateName }}</span>
                </div>
              </div>
              <button
                v-if="job.active"
                @click="generationStore.cancelGeneration(job.templateId)"
                class="text-sm text-red-500 hover:text-red-700 font-medium"
              >
                {{ t('cancel') }}
              </button>
              <button
                v-else
                @click="generationStore.closeProgress(job.templateId)"
                class="text-sm text-gray-500 hover:text-gray-700"
              >
                {{ t('close') }}
              </button>
            </div>

            <!-- Progress Bar -->
            <div class="px-6 py-4">
              <div class="flex justify-between text-sm text-gray-600 mb-2">
                <span>{{ t('progress') }}</span>
                <span>{{ job.completedCount + job.failedCount + job.cancelledCount }} / {{ job.totalRecords }}</span>
              </div>
              <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  class="h-full transition-all duration-300 ease-out"
                  :class="job.failedCount > 0 ? 'bg-orange-500' : 'bg-blue-600'"
                  :style="{ width: `${((job.completedCount + job.failedCount + job.cancelledCount) / job.totalRecords) * 100}%` }"
                ></div>
              </div>

              <!-- Stats -->
              <div class="flex gap-6 mt-4 text-sm">
                <div class="flex items-center gap-2">
                  <CheckCircle :size="16" class="text-green-600" />
                  <span class="text-gray-600">{{ t('success') }}: {{ job.completedCount }}</span>
                </div>
                <div v-if="job.warningCount > 0" class="flex items-center gap-2">
                  <AlertCircle :size="16" class="text-yellow-600" />
                  <span class="text-gray-600">{{ t('warnings') }}: {{ job.warningCount }}</span>
                </div>
                <div v-if="job.failedCount > 0" class="flex items-center gap-2">
                  <XCircle :size="16" class="text-red-600" />
                  <span class="text-gray-600">{{ t('failed') }}: {{ job.failedCount }}</span>
                </div>
              </div>
            </div>

            <!-- Document List - Always visible -->
            <div class="px-6 py-4 border-t border-gray-200">
              <div class="text-sm font-medium text-gray-700 mb-3">{{ t('documents') }} ({{ job.generations.length }})</div>
              <div class="space-y-2 max-h-64 overflow-y-auto">
                <div
                  v-for="gen in job.generations"
                  :key="gen.generationId"
                  class="flex items-center gap-3 p-2 rounded-lg"
                  :class="{
                    'bg-gray-50': gen.status === 'pending' && !gen.currentStep,
                    'bg-blue-50': gen.status === 'processing' || gen.status === 'uploading' || (gen.status === 'pending' && gen.currentStep),
                    'bg-green-50': gen.status === 'completed',
                    'bg-red-50': gen.status === 'failed',
                    'bg-gray-100': gen.status === 'cancelled'
                  }"
                >
                  <!-- Status Icon -->
                  <div class="flex-shrink-0">
                    <Loader2 v-if="gen.status === 'pending' && gen.currentStep" :size="20" class="animate-spin text-blue-600" />
                    <div v-else-if="gen.status === 'pending'" class="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                    <Loader2 v-else-if="gen.status === 'processing' || gen.status === 'uploading'" :size="20" class="animate-spin text-blue-600" />
                    <CheckCircle v-else-if="gen.status === 'completed'" :size="20" class="text-green-600" />
                    <XCircle v-else-if="gen.status === 'failed'" :size="20" class="text-red-600" />
                    <div v-else class="w-5 h-5 rounded-full bg-gray-300"></div>
                  </div>

                  <!-- Content -->
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-700 truncate">
                      {{ gen.filename || gen.generationId.slice(0, 8) }}
                    </div>
                    <div v-if="gen.currentStep" class="text-xs text-gray-500 truncate">
                      {{ gen.currentStep }}
                    </div>
                    <div v-else-if="gen.status === 'failed'" class="text-xs text-red-600 truncate">
                      {{ gen.error || t('generationFailed') }}
                    </div>
                    <!-- Warnings list (shown inline when completed with warnings) -->
                    <div v-if="gen.hadWarnings && gen.status === 'completed' && gen.warnings?.length" class="mt-1">
                      <div v-for="(warning, idx) in gen.warnings" :key="idx" class="text-xs text-yellow-600 truncate">
                        ⚠ {{ warning }}
                      </div>
                    </div>
                  </div>

                  <!-- Download Button -->
                  <button
                    v-if="gen.status === 'completed' && gen.downloadUrl"
                    @click="handleDownload(gen.downloadUrl!, gen.filename || 'document')"
                    class="flex-shrink-0 p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded"
                  >
                    <Download :size="18" />
                  </button>

                  <!-- Warning Badge -->
                  <AlertCircle
                    v-if="gen.hadWarnings && gen.status === 'completed'"
                    :size="16"
                    class="flex-shrink-0 text-yellow-500"
                  />
                </div>

                <!-- Empty state -->
                <div v-if="job.generations.length === 0" class="text-center text-gray-400 py-4">
                  {{ t('processing') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped>
/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
